import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { PaymentStatus } from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

export type SubscriptionIcons = 'ok' | 'warn' | 'close' | 'slash';
type SubscriptionStatusUI = {
  iconType: null | SubscriptionIcons;
  text: string;
  tooltip: string;
};

const JOB_SUBSCRIPTION_DAYS_TO_WARN = 7;
const FAILED_STATUSES = [
  PaymentStatus.Unpaid,
  PaymentStatus.Paused,
  PaymentStatus.Trialing,
  PaymentStatus.Incomplete,
  PaymentStatus.IncompleteExpired,
  PaymentStatus.PastDue
];

export const mapSubscriptionToStatusUI = (
  t: TFunction<'common', undefined>,
  status: PaymentStatus | null,
  dueDate: string | null
): SubscriptionStatusUI => {
  const defaultData: SubscriptionStatusUI = {
    iconType: null,
    text: FALLBACK_VALUE,
    tooltip: ''
  };

  if (!status) {
    return defaultData;
  }

  switch (status) {
    case PaymentStatus.Active:
      if (!dueDate) return defaultData;

      const isSubscriptionDueDateTomorrow = dayjs(dueDate).isSame(
        dayjs().add(1, 'day'),
        'day'
      );
      const daysRemaining = dayjs(dueDate).diff(dayjs(), 'days');
      const daysRemainingCorrected =
        daysRemaining >= 0 ? daysRemaining : FALLBACK_VALUE;

      const subscriptionOverSoon =
        daysRemainingCorrected === 0 || isSubscriptionDueDateTomorrow;

      const isSafePeriod = daysRemaining > JOB_SUBSCRIPTION_DAYS_TO_WARN;
      return {
        tooltip: isSafePeriod
          ? t('jobSubscriptionStatus.tooltip.active.safe')
          : t('jobSubscriptionStatus.tooltip.active.notSafe'),
        text: subscriptionOverSoon
          ? t('jobSubscriptionStatus.text.active.dueDate')
          : t('jobSubscriptionStatus.text.active.notSoon', {
              count: +daysRemainingCorrected
            }),
        iconType: isSafePeriod ? 'ok' : 'warn'
      };
    case PaymentStatus.Canceled:
      return {
        iconType: 'close',
        text: t('jobSubscriptionStatus.text.canceled'),
        tooltip: t('jobSubscriptionStatus.tooltip.canceled')
      };
    case PaymentStatus.Unpaid:
    case PaymentStatus.Paused:
    case PaymentStatus.Trialing:
    case PaymentStatus.Incomplete:
    case PaymentStatus.IncompleteExpired:
    case PaymentStatus.PastDue:
      return {
        tooltip: '',
        iconType: 'slash',
        text: t('jobSubscriptionStatus.text.pastDue')
      };
    default:
      return defaultData;
  }
};
